import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiX, FiHome, FiUser, FiVideo, FiUsers, FiCreditCard, FiSettings, FiHelpCircle, FiLogIn, FiUserPlus } from 'react-icons/fi';
import { ROUTES } from '../../utils/constants';
import { useAuth } from '../../hooks';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../common';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu: React.FC<MobileNavProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { logout, user, isAuthenticated } = useAuth();
  
  // Close menu when route changes
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);
  
  const authenticatedNavItems = [
    { label: 'Dashboard', to: ROUTES.DASHBOARD, icon: <FiHome /> },
    { label: 'Profile', to: ROUTES.PROFILE, icon: <FiUser /> },
    { label: 'Host Meeting', to: ROUTES.HOST_MEETING, icon: <FiVideo /> },
    { label: 'Join Meeting', to: ROUTES.JOIN_MEETING, icon: <FiUsers /> },
    { label: 'Meeting Passes', to: ROUTES.MEETING_PASSES, icon: <FiCreditCard /> },
    { label: 'Settings', to: ROUTES.SETTINGS, icon: <FiSettings /> },
    { label: 'Help & Support', to: ROUTES.HELP, icon: <FiHelpCircle /> },
  ];
  
  const unauthenticatedNavItems = [
    { label: 'Home', to: ROUTES.HOME, icon: <FiHome /> },
    { label: 'Use Cases', to: ROUTES.USE_CASES, icon: <FiUsers /> },
  ];
  
  const navItems = isAuthenticated ? authenticatedNavItems : unauthenticatedNavItems;
  
  const isActive = (path: string) => location.pathname === path;
  
  return (
    <>
      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
              onClick={onClose}
            />
            
            {/* Menu */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 right-0 w-64 h-full bg-background-glass backdrop-blur-lg z-50 border-l border-white/10 flex flex-col"
            >
              {/* Close Button */}
              <div className="absolute top-4 right-4">
                <button
                  onClick={onClose}
                  className="p-2 rounded-full bg-background-glass backdrop-blur-sm border border-white/10"
                  aria-label="Close menu"
                >
                  <FiX className="text-white text-xl" />
                </button>
              </div>
              
              {/* User Info or Auth Buttons */}
              <div className="p-6 border-b border-white/10">
                {isAuthenticated ? (
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold">
                      {user?.name ? user.name.charAt(0) : 'U'}
                    </div>
                    <div className="ml-3">
                      <p className="font-medium text-white">{user?.name || 'User'}</p>
                      <p className="text-sm text-white/60">{user?.email || user?.phone}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col space-y-2">
                    <Link to={ROUTES.LOGIN}>
                      <Button
                        variant="glass"
                        size="sm"
                        leftIcon={<FiLogIn />}
                        className="w-full"
                      >
                        Login
                      </Button>
                    </Link>
                    <Link to={ROUTES.SIGNUP}>
                      <Button
                        variant="primary"
                        size="sm"
                        glowEffect
                        leftIcon={<FiUserPlus />}
                        className="w-full"
                      >
                        Sign Up
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
              
              {/* Navigation Links */}
              <nav className="flex-1 overflow-y-auto py-4">
                <ul className="space-y-1 px-3">
                  {navItems.map((item, index) => (
                    <li key={index}>
                      <Link
                        to={item.to}
                        className={`flex items-center px-4 py-3 rounded-lg ${
                          isActive(item.to)
                            ? 'bg-primary-600/20 text-primary-400 font-medium'
                            : 'text-white/70 hover:bg-white/5 hover:text-white'
                        }`}
                        onClick={onClose}
                      >
                        <span className="mr-3">{item.icon}</span>
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
              
              {/* Logout Button (only for authenticated users) */}
              {isAuthenticated && (
                <div className="p-4 border-t border-white/10">
                  <button
                    onClick={() => {
                      logout();
                      onClose();
                    }}
                    className="w-full px-4 py-3 text-left rounded-lg text-white/70 hover:bg-white/5 hover:text-white flex items-center"
                  >
                    <FiX className="mr-3" />
                    Logout
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileMenu;
